import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import ProfessorCard from "../components/ProfessorCard";
import ProfessorPoolFilterBar from "../components/ProfessorPool/ProfessorPoolFilterBar";
import ContactModal from "../components/ContactModal/ContactModal";
import { useDebounce } from "../hooks/useDebounce";
import "../components/ProfessorPool/ProfessorPool.css";

export default function ProfessorPoolPage() {
  const [professors, setProfessors] = useState([]);
  const [filters, setFilters] = useState({
    name: "",
    interests: "",
    institutions: [],
    departments: [],
  });
  const [sortBy, setSortBy] = useState("relevance");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProfessor, setSelectedProfessor] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Debounce text filters to reduce API calls
  const debouncedFilters = useDebounce(filters, 500);

  const pageSize = 12;

  const fetchProfessors = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const query = new URLSearchParams({
        role: "professor",
        page: currentPage,
        limit: pageSize,
        sortBy: sortBy,
      });

      // Add text filters (using debounced values)
      if (debouncedFilters.name?.trim()) {
        query.append("name", debouncedFilters.name.trim());
      }
      if (debouncedFilters.interests?.trim()) {
        query.append("interests", debouncedFilters.interests.trim());
      }

      // Handle array filters (these don't need debouncing)
      debouncedFilters.institutions.forEach((inst) =>
        query.append("institutions", inst)
      );
      debouncedFilters.departments.forEach((dept) => query.append("departments", dept));

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users?${query.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setProfessors(response.data.users || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching professors:", error);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedFilters, currentPage, sortBy]);

  useEffect(() => {
    fetchProfessors();
  }, [fetchProfessors]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1); // Reset to first page when sort changes
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="professor-pool-pagination-container">
        <div className="professor-pool-pagination-controls">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="professor-pool-pagination-button"
          >
            <svg
              className="professor-pool-pagination-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            Previous
          </button>

          <div className="professor-pool-pagination-numbers">
            {pages.map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`professor-pool-pagination-number ${page === currentPage ? "active" : ""
                  }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="professor-pool-pagination-button"
          >
            Next
            <svg
              className="professor-pool-pagination-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        <p className="professor-pool-pagination-info">
          Page {currentPage} of {totalPages}
        </p>
      </div>
    );
  };

  return (
    <div className="professor-pool">
      <div className="professor-pool-container">
        {/* Filter Sidebar */}
        <div className="professor-pool-filter-sidebar">
          <ProfessorPoolFilterBar onFilterChange={handleFilterChange} />
        </div>

        {/* Main Content */}
        <div className="professor-pool-main-content">
          <div className="professor-pool-header">
            <div className="professor-pool-header-top">
              <div className="professor-pool-title-section">
                <h1 className="professor-pool-title">Professor Pool</h1>
                <p className="professor-pool-subtitle">
                  Browse professors and learn about their research interests,
                  institution, and more
                </p>
              </div>

              {/* Sort Dropdown */}
              <div className="professor-pool-sort-section">
                <label htmlFor="sort-select" className="sort-label">Sort by:</label>
                <select
                  id="sort-select"
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="sort-select"
                >
                  <option value="relevance">Relevance</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="recent">Recently Active</option>
                </select>
              </div>
            </div>

            {/* Result Count and Active Filters */}
            <div className="professor-pool-header-bottom">
              <div className="result-count">
                {isLoading ? (
                  <span className="result-count-loading">Searching...</span>
                ) : (
                  <span className="result-count-text">
                    {totalCount} {totalCount === 1 ? 'professor' : 'professors'} found
                  </span>
                )}
              </div>

              {/* Active Filter Badges */}
              {(filters.name || filters.interests || filters.institutions.length > 0 ||
                filters.departments.length > 0) && (
                  <div className="active-filters-section">
                    <span className="active-filters-label">Active filters:</span>
                    <div className="active-filters-badges">
                      {filters.name && (
                        <span className="filter-badge">
                          Name: {filters.name}
                          <button
                            onClick={() => setFilters({ ...filters, name: "" })}
                            className="filter-badge-remove"
                            aria-label="Remove name filter"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.interests && (
                        <span className="filter-badge">
                          Interests: {filters.interests}
                          <button
                            onClick={() => setFilters({ ...filters, interests: "" })}
                            className="filter-badge-remove"
                            aria-label="Remove interests filter"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {filters.institutions.map((inst) => (
                        <span key={inst} className="filter-badge">
                          Institution: {inst}
                          <button
                            onClick={() =>
                              setFilters({
                                ...filters,
                                institutions: filters.institutions.filter((i) => i !== inst),
                              })
                            }
                            className="filter-badge-remove"
                            aria-label={`Remove ${inst} filter`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {filters.departments.map((dept) => (
                        <span key={dept} className="filter-badge">
                          Department: {dept}
                          <button
                            onClick={() =>
                              setFilters({
                                ...filters,
                                departments: filters.departments.filter((d) => d !== dept),
                              })
                            }
                            className="filter-badge-remove"
                            aria-label={`Remove ${dept} filter`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>

          {isLoading ? (
            <div className="professor-pool-loading-container">
              <div className="professor-pool-loading-spinner"></div>
              <p className="professor-pool-loading-text">
                Loading professors...
              </p>
            </div>
          ) : (
            <>
              {professors.length === 0 ? (
                <div className="professor-pool-empty">
                  <svg
                    className="professor-pool-empty-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                  <h3 className="professor-pool-empty-title">
                    No professors found
                  </h3>
                  <p className="professor-pool-empty-description">
                    Try adjusting your filters to find more professors
                  </p>
                </div>
              ) : (
                <div className="professor-pool-grid">
                  {professors.map((professor) => (
                    <div
                      key={professor._id}
                      className="professor-pool-grid-item"
                    >
                      <ProfessorCard
                        professor={professor}
                        onContact={() => {
                          setSelectedProfessor(professor);
                          setIsModalOpen(true);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {/* Contact Modal */}
      <ContactModal
        professor={selectedProfessor}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProfessor(null);
        }}
        onMessageSent={() => {
          // Optionally refresh conversations or show a success message
          console.log("Message sent successfully");
        }}
      />
    </div>
  );
}
